import { useState, useEffect, useRef } from 'react'

/**
 * Debounces a value in a React component, optionally calling a callback when the debounced value updates.
 *
 * @template T - The type of the value.
 * @param {T} value - The value to debounce.
 * @param {number} [delay=500] - The delay in milliseconds before updating the debounced value.
 * @param {(value: T) => void} [onDebounce] - Optional callback invoked with the debounced value (skips initial render).
 * @returns {T} - The debounced value.
 */
export function useDebounce<T>(value: T, delay?: number, onDebounce?: (value: T) => void): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  const isFirstRender = useRef(true)

  useEffect((): (() => void) => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500)

    return () => clearTimeout(timer)
  }, [value, delay])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (onDebounce) onDebounce(debouncedValue)
  }, [debouncedValue])

  return debouncedValue
}
